import React, { useEffect, useState } from 'react';
import { getDepartments, getEmployees, setDepartmentHead } from '../services/employeeService';
import useAuthStore from '../store/authStore';
import useUIStore from '../store/uiStore';

// Department heads (approved layout 2026-10-24). The head gets project
// schedule reminders and can update the department's schedule lines. Saves
// on pick; only CEO/System_Admin can change it (also enforced server-side).
const ADMIN_ROLES = ['CEO', 'System_Admin'];

const DepartmentHeadsCard = () => {
  const user = useAuthStore((s) => s.user);
  const showToast = useUIStore((s) => s.showToast);
  const canEdit = ADMIN_ROLES.includes(user?.role);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [savingId, setSavingId] = useState(null);

  const load = () =>
    getDepartments()
      .then((res) => setDepartments(res.data.departments || []))
      .catch(() => showToast('Failed to load departments', 'error'));

  useEffect(() => {
    load();
    getEmployees().then((res) => setEmployees(res.data.employees || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = async (dept, value) => {
    setSavingId(dept.id);
    try {
      await setDepartmentHead(dept.id, value ? Number(value) : null);
      showToast(`${dept.name} head updated`, 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update head', 'error');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="glass-card p-6 mb-6">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <h2 className="font-semibold text-gray-900">Department heads</h2>
        <p className="text-xs text-gray-500">The head gets schedule reminders and can update the department's schedule tasks</p>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 border-b">
            <th className="py-2 pr-2 w-1/3">Department</th>
            <th className="py-2 pr-2">Head</th>
            <th className="py-2 w-28 hidden sm:table-cell" />
          </tr>
        </thead>
        <tbody>
          {departments.map((d) => (
            <tr key={d.id} className="border-b border-gray-100">
              <td className="py-2 pr-2">{d.name}</td>
              <td className="py-2 pr-2">
                {canEdit ? (
                  <select className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" value={d.hod_id || ''}
                    disabled={savingId === d.id} onChange={(e) => handleChange(d, e.target.value)}>
                    <option value="">Select head</option>
                    {employees.map((e) => <option key={e.id} value={e.id}>{e.name} ({e.role})</option>)}
                  </select>
                ) : (
                  <span>{d.hod_name || '—'}</span>
                )}
                {!d.hod_id && <div className="text-xs text-amber-700 mt-1 sm:hidden">No head set</div>}
              </td>
              <td className="py-2 text-xs text-amber-700 hidden sm:table-cell">{d.hod_id ? '' : 'No head set'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-gray-500 mt-3">
        Saves as soon as a head is picked. Any active employee can be a head, from any department.
        {!canEdit && ' Only the CEO or a system admin can change heads.'}
      </p>
    </div>
  );
};

export default DepartmentHeadsCard;
